import { useState, useEffect, useCallback } from 'react';
import type { SajuResult } from '@/utils/sajuCalculations';

// 히스토리 아이템 타입
export interface SajuHistoryItem {
  id: string;
  timestamp: number;
  year: number;
  month: number;
  day: number;
  hour: number | null;
  minute: number;
  unknownTime: boolean;
  locationName: string;
  saju: SajuResult;
  note?: string;
}

const STORAGE_KEY = 'baren-saju-history';
const MAX_HISTORY = 20;

// 히스토리 관리 훅
export function useSajuHistory() {
  const [history, setHistory] = useState<SajuHistoryItem[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // 초기 로드
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setHistory(parsed);
      }
    } catch (e) {
      console.error('Failed to load history:', e);
    }
    setIsLoaded(true);
  }, []);

  // 저장
  const saveHistory = useCallback((items: SajuHistoryItem[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      console.error('Failed to save history:', e);
    }
  }, []);

  // 추가
  const addToHistory = useCallback((item: Omit<SajuHistoryItem, 'id' | 'timestamp'>) => {
    const newItem: SajuHistoryItem = {
      ...item,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
    };

    setHistory(prev => {
      // 중복 체크 (같은 날짜/시간/장소)
      const isDuplicate = prev.some(
        h => h.year === item.year && 
             h.month === item.month && 
             h.day === item.day &&
             h.hour === item.hour &&
             h.unknownTime === item.unknownTime
      );
      
      if (isDuplicate) {
        // 기존 항목 업데이트
        const filtered = prev.filter(
          h => !(h.year === item.year && 
                 h.month === item.month && 
                 h.day === item.day &&
                 h.hour === item.hour &&
                 h.unknownTime === item.unknownTime)
        );
        const newHistory = [newItem, ...filtered].slice(0, MAX_HISTORY);
        saveHistory(newHistory);
        return newHistory;
      }

      const newHistory = [newItem, ...prev].slice(0, MAX_HISTORY);
      saveHistory(newHistory);
      return newHistory;
    });
  }, [saveHistory]);

  // 삭제
  const removeFromHistory = useCallback((id: string) => {
    setHistory(prev => {
      const newHistory = prev.filter(h => h.id !== id);
      saveHistory(newHistory);
      return newHistory;
    });
  }, [saveHistory]);

  // 전체 삭제
  const clearHistory = useCallback(() => {
    setHistory([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // 메모 업데이트
  const updateNote = useCallback((id: string, note: string) => {
    setHistory(prev => {
      const newHistory = prev.map(h => 
        h.id === id ? { ...h, note } : h
      );
      saveHistory(newHistory);
      return newHistory;
    });
  }, [saveHistory]);

  // 특정 아이템 가져오기
  const getHistoryItem = useCallback((id: string) => {
    return history.find(h => h.id === id);
  }, [history]);

  return {
    history,
    isLoaded,
    addToHistory,
    removeFromHistory,
    clearHistory,
    updateNote,
    getHistoryItem,
  };
}